import { useState, useCallback, useRef, useEffect, Fragment } from "react";
import { format } from "date-fns";
import { ChevronDown, ChevronRight, Copy, Check, Undo2 } from "lucide-react";
import { useAdminUserLedger } from "../hooks/useUsers";
import type {
  KoinsSourceType,
  KoinsTransactionKind,
  KoinsLedgerEntry,
} from "../types";
import {
  SOURCE_TYPE_CONFIG,
  FILTERABLE_SOURCE_TYPES,
} from "../types";
import { useLocale } from "../../../context/LanguageContext";

interface UserLedgerTableProps {
  userId: string;
}

function parseMeta(
  meta: string | undefined,
): Record<string, unknown> | null {
  if (!meta) return null;
  try {
    return JSON.parse(meta) as Record<string, unknown>;
  } catch {
    return null;
  }
}

// ─── Kind badge colors ──────────────────────────────────────────────────────

const KIND_BADGE: Record<KoinsTransactionKind, { labelKey: string; className: string }> = {
  EARN: { labelKey: "users.kind.EARN", className: "bg-success/15 text-success" },
  SPEND: { labelKey: "users.kind.SPEND", className: "bg-destructive/15 text-destructive" },
  ADJUSTMENT: { labelKey: "users.kind.ADJUSTMENT", className: "bg-warning/15 text-warning" },
  REVERSAL: { labelKey: "users.kind.REVERSAL", className: "bg-muted text-muted-foreground" },
};

// ─── Source type → contextual entity label-key for sourceId ─────────────────

const SOURCE_ID_LABEL_KEYS: Partial<Record<KoinsSourceType, string>> = {
  PHYSICAL_ACTIVITY: "users.sourceIdLabel.activity",
  TELEMETRY_STEPS: "users.sourceIdLabel.providerDate",
  CHALLENGE_CREATE: "users.sourceIdLabel.challenge",
  CHALLENGE_BUY_IN: "users.sourceIdLabel.challenge",
  CHALLENGE_PRIZE: "users.sourceIdLabel.challenge",
  CHALLENGE_REFUND: "users.sourceIdLabel.challenge",
  CHALLENGE_BURN: "users.sourceIdLabel.challenge",
  EVENT_ATTEND: "users.sourceIdLabel.event",
  MARKETPLACE: "users.sourceIdLabel.itemOrder",
  ORGANIZATION_BONUS: "users.sourceIdLabel.organization",
};

// ─── Metadata display helpers ───────────────────────────────────────────────

const META_DISPLAY_LABEL_KEYS: Record<string, string> = {
  activityDisplayName: "users.meta.activityDisplayName",
  activityName: "users.meta.activityName",
  activityType: "users.meta.activityType",
  duration: "users.meta.duration",
  metValue: "users.meta.metValue",
  capApplied: "users.meta.capApplied",
  source: "users.meta.source",
  provider: "users.meta.provider",
  date: "users.meta.date",
  eligibleSteps: "users.meta.eligibleSteps",
  calcVersion: "users.meta.calcVersion",
  reason: "users.meta.reason",
  challengeId: "users.meta.challengeId",
  challengeName: "users.meta.challengeName",
  entryFee: "users.meta.entryFee",
  position: "users.meta.position",
  burnAmount: "users.meta.burnAmount",
  marketplaceItemId: "users.meta.marketplaceItemId",
  cancelledBy: "users.meta.cancelledBy",
};

function formatMetaValue(
  value: unknown,
  t: (key: string) => string,
): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? t("users.value.yes") : t("users.value.no");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

// ─── Copy-to-clipboard button ───────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const { t } = useLocale();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [text]);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        handleCopy();
      }}
      className="ml-1 inline-flex items-center rounded p-0.5 text-muted-foreground hover:text-foreground"
      title={t("adminUI.action.copyToClipboard")}
    >
      {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

// ─── Expandable ledger entry detail card ────────────────────────────────────

function LedgerEntryDetail({ entry }: { entry: KoinsLedgerEntry }) {
  const { t } = useLocale();
  const meta = parseMeta(entry.meta);
  const sourceIdLabel = t(
    SOURCE_ID_LABEL_KEYS[entry.sourceType] ?? "users.sourceIdLabel.default",
  );

  // Separate "breakdown" from other meta keys (breakdown is often a complex object)
  const metaEntries: [string, string][] = meta
    ? Object.entries(meta)
        .filter(([k]) => k !== "breakdown")
        .map(([k, v]) => [k, formatMetaValue(v, t)])
    : [];
  const breakdown = meta?.breakdown;

  return (
    <div className="border-t border-border bg-muted/30 px-4 py-3 text-xs">
      <p className="mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        {t("users.ledger.detailTitle")}
      </p>

      {/* Key-value grid */}
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
        {/* Entry ID */}
        <span className="text-muted-foreground">{t("users.ledger.entryId")}</span>
        <span className="flex items-center gap-1 font-mono text-foreground">
          {entry._id.slice(0, 10)}…{entry._id.slice(-4)}
          <CopyButton text={entry._id} />
        </span>

        {/* Kind */}
        <span className="text-muted-foreground">{t("users.ledger.kind")}</span>
        <span className="text-foreground">
          {t(KIND_BADGE[entry.kind]?.labelKey ?? entry.kind)}
        </span>

        {/* Source Type */}
        <span className="text-muted-foreground">{t("users.ledger.sourceType")}</span>
        <span className="text-foreground">
          {t(SOURCE_TYPE_CONFIG[entry.sourceType]?.labelKey ?? entry.sourceType)}
        </span>

        {/* Source ID */}
        {entry.sourceId && (
          <>
            <span className="text-muted-foreground">{sourceIdLabel}</span>
            <span className="flex items-center gap-1 font-mono text-foreground">
              {entry.sourceId.length > 28
                ? `${entry.sourceId.slice(0, 14)}…${entry.sourceId.slice(-8)}`
                : entry.sourceId}
              <CopyButton text={entry.sourceId} />
            </span>
          </>
        )}

        {/* Effective At (full precision) */}
        <span className="text-muted-foreground">{t("users.ledger.effectiveAt")}</span>
        <span className="text-foreground">
          {format(new Date(entry.effectiveAt), "MMM d, yyyy HH:mm:ss")} UTC
        </span>

        {/* Created At */}
        <span className="text-muted-foreground">{t("users.ledger.createdAt")}</span>
        <span className="text-foreground">
          {format(new Date(entry.createdAt), "MMM d, yyyy HH:mm:ss")} UTC
        </span>

        {/* Idempotency Key */}
        <span className="text-muted-foreground">{t("users.ledger.idempotencyKey")}</span>
        <span className="flex items-center gap-1 font-mono text-foreground">
          {entry.idempotencyKey.length > 28
            ? `${entry.idempotencyKey.slice(0, 14)}…${entry.idempotencyKey.slice(-8)}`
            : entry.idempotencyKey}
          <CopyButton text={entry.idempotencyKey} />
        </span>

        {/* Organization */}
        {entry.organizationId && (
          <>
            <span className="text-muted-foreground">{t("users.ledger.organization")}</span>
            <span className="flex items-center gap-1 font-mono text-foreground">
              {entry.organizationId.slice(0, 10)}…{entry.organizationId.slice(-4)}
              <CopyButton text={entry.organizationId} />
            </span>
          </>
        )}
      </div>

      {/* Metadata section */}
      {metaEntries.length > 0 && (
        <div className="mt-3 border-t border-border pt-2">
          <p className="mb-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t("users.ledger.metadata")}
          </p>
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            {metaEntries.map(([key, displayValue]) => (
              <Fragment key={key}>
                <span className="text-muted-foreground">
                  {META_DISPLAY_LABEL_KEYS[key] ? t(META_DISPLAY_LABEL_KEYS[key]) : key}
                </span>
                <span className="text-foreground break-all">
                  {displayValue}
                </span>
              </Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Breakdown (raw JSON toggle) */}
      {breakdown != null && (
        <div className="mt-2 border-t border-border pt-2">
          <details className="group">
            <summary className="cursor-pointer text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground">
              {t("users.ledger.calcBreakdown")}
            </summary>
            <pre className="mt-1 max-h-32 overflow-auto rounded bg-muted p-2 font-mono text-[11px] text-foreground">
              {JSON.stringify(breakdown, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {/* Reversal chain */}
      {entry.reversalOfLedgerEntryId && (
        <div className="mt-2 flex items-center gap-1.5 rounded border border-warning/30 bg-warning/10 px-2.5 py-1.5">
          <Undo2 className="h-3.5 w-3.5 text-warning" />
          <span className="text-xs text-warning">
            {t("users.ledger.reversalOf")}
          </span>
          <span className="font-mono text-xs text-foreground">
            {entry.reversalOfLedgerEntryId.slice(0, 10)}…{entry.reversalOfLedgerEntryId.slice(-4)}
          </span>
          <CopyButton text={entry.reversalOfLedgerEntryId} />
        </div>
      )}
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export function UserLedgerTable({ userId }: UserLedgerTableProps) {
  const { t } = useLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const [sourceFilter, setSourceFilter] = useState<
    KoinsSourceType | undefined
  >(undefined);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, loading, fetchMore } = useAdminUserLedger(userId, {
    first: 20,
    after: cursor,
    sourceType: sourceFilter,
  });

  const ledger = data?.adminUserLedger;
  const entries = ledger?.edges ?? [];
  const pageInfo = ledger?.pageInfo;
  const totalCount = ledger?.totalCount ?? 0;

  const handleFilterChange = useCallback(
    (value: string) => {
      setCursor(undefined);
      setExpandedId(null);
      setSourceFilter(
        value === "ALL" ? undefined : (value as KoinsSourceType),
      );
    },
    [],
  );

  // Auto-scroll to ledger section when filter changes
  useEffect(() => {
    if (sourceFilter !== undefined) {
      containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [sourceFilter]);

  const handleLoadMore = useCallback(() => {
    if (!pageInfo?.endCursor || !pageInfo?.hasNextPage) return;
    fetchMore({
      variables: { after: pageInfo.endCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          adminUserLedger: {
            ...fetchMoreResult.adminUserLedger,
            edges: [
              ...prev.adminUserLedger.edges,
              ...fetchMoreResult.adminUserLedger.edges,
            ],
          },
        };
      },
    });
  }, [pageInfo, fetchMore]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <div ref={containerRef} className="space-y-3">
      {/* Source Type Filter Chips */}
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => handleFilterChange("ALL")}
          className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
            !sourceFilter
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
        >
          {t("users.ledger.filterAll")}
        </button>
        {FILTERABLE_SOURCE_TYPES.map((st) => {
          const config = SOURCE_TYPE_CONFIG[st];
          return (
            <button
              key={st}
              type="button"
              onClick={() => handleFilterChange(st)}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                sourceFilter === st
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {config.emoji} {t(config.labelKey)}
            </button>
          );
        })}
      </div>

      {/* Count */}
      <p className="text-xs text-muted-foreground">
        {t("users.ledger.transactionCount", { count: totalCount })}
        {sourceFilter
          ? ` (${t(SOURCE_TYPE_CONFIG[sourceFilter].labelKey)})`
          : ""}
      </p>

      {/* Loading */}
      {loading && entries.length === 0 && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-md border border-border bg-muted"
            />
          ))}
        </div>
      )}

      {/* Empty */}
      {!loading && entries.length === 0 && (
        <div className="rounded-md border border-dashed border-border py-8 text-center">
          <p className="text-sm text-muted-foreground">
            {t("users.ledger.empty")}
          </p>
        </div>
      )}

      {/* Ledger entries (bank statement style with expandable detail) */}
      {entries.length > 0 && (
        <div className="rounded-md border border-border divide-y divide-border">
          {entries.map((entry: KoinsLedgerEntry) => {
            const config =
              SOURCE_TYPE_CONFIG[entry.sourceType] ??
              SOURCE_TYPE_CONFIG.ADJUSTMENT;
            const meta = parseMeta(entry.meta);
            const isPositive = entry.amount > 0;
            const isExpanded = expandedId === entry._id;
            const kindBadge = KIND_BADGE[entry.kind] ?? KIND_BADGE.ADJUSTMENT;

            return (
              <div key={entry._id}>
                {/* Summary row — clickable */}
                <button
                  type="button"
                  onClick={() => toggleExpand(entry._id)}
                  className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/40 ${
                    isExpanded ? "bg-muted/30" : ""
                  }`}
                >
                  {/* Expand chevron */}
                  <ChevronRight
                    className={`h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-transform ${
                      isExpanded ? "rotate-90" : ""
                    }`}
                  />

                  {/* Icon */}
                  <span className="text-base flex-shrink-0">{config.emoji}</span>

                  {/* Details */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground">
                        {t(config.labelKey)}
                      </p>
                      <span
                        className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none ${kindBadge.className}`}
                      >
                        {t(kindBadge.labelKey)}
                      </span>
                      {entry.reversalOfLedgerEntryId && (
                        <Undo2 className="h-3 w-3 text-warning" />
                      )}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {format(
                        new Date(entry.effectiveAt),
                        "MMM d, yyyy HH:mm",
                      )}
                      {meta?.reason
                        ? ` · ${String(meta.reason)}`
                        : meta?.activityDisplayName
                          ? ` · ${String(meta.activityDisplayName)}`
                          : meta?.challengeName
                            ? ` · ${String(meta.challengeName)}`
                            : ""}
                    </p>
                  </div>

                  {/* Amount */}
                  <span
                    className={`flex-shrink-0 text-right font-mono text-sm font-semibold ${
                      isPositive
                        ? "text-success"
                        : "text-destructive"
                    }`}
                  >
                    {isPositive ? "+" : ""}
                    {entry.amount.toLocaleString()}
                  </span>
                </button>

                {/* Expandable detail */}
                {isExpanded && <LedgerEntryDetail entry={entry} />}
              </div>
            );
          })}
        </div>
      )}

      {/* Load More */}
      {pageInfo?.hasNextPage && (
        <button
          type="button"
          onClick={handleLoadMore}
          disabled={loading}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-border py-2 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
        >
          <ChevronDown className="h-4 w-4" />
          {t("users.ledger.loadMore")}
        </button>
      )}
    </div>
  );
}
